import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { ArrowLeft, BookOpen, Check, ChevronRight, Compass, Layers, Route } from 'lucide-react';
import * as Immutable from 'immutable';
import * as appActions from 'Actions/app';
import * as repertoireActions from 'Actions/repertoire';
import type { RootState } from 'Reducers';
import {
  selectCurriculumGraph,
  type CurriculumState,
  type RoadmapLevelStage,
  type RoadmapPiece,
} from 'Selectors/curriculum';
import { Menu, MobileMenuButton } from 'Components/RoadmapComponents/Menu';
import {
  RoadmapDetails,
  type RoadmapSelection,
} from 'Components/RoadmapComponents/stages/RoadmapDetails';
import { RepertoireStage } from 'Components/RoadmapComponents/stages/RepertoireStage';
import { Ribbon } from 'Components/RoadmapComponents/stages/StageArtwork';
import { RoadmapMinimap } from 'Components/RoadmapComponents/stages/RoadmapMinimap';
import { RoadmapScene } from 'Components/RoadmapComponents/stages/RoadmapScene';
import { UnitPicker } from 'Components/RoadmapComponents/stages/UnitPicker';
import {
  journeyLayout,
  overviewPath,
  overviewPoints,
} from 'Components/RoadmapComponents/stages/geometry';
import { isCapacitorNative } from 'Utils/Constants';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';
import { type CompletedChapters, resolvePlayRoute } from 'Utils/tutorialCompletion';
import { isSpaceForThePage } from 'Components/RoadmapComponents/roadmapKeys';
import Survey from './Survey';
import Confirmation from 'Containers/Payment/Confirmation';
import './RoadmapStages.css';

type Visit = {
  selection: RoadmapSelection;
  scroll: number;
  showStages: boolean;
  explored: number[];
};
// Route-local view state: leaving to play and coming back restores the same stage.
// Keyed by learner and unit; nothing is written to the account or persisted catalog.
const visits = new Map<string, Visit>();

function Overview({
  graph,
  width,
  selected,
  onLesson,
  onBranch,
}: {
  graph: RoadmapLevelStage[];
  width: number;
  selected: number;
  onLesson: (stage: RoadmapLevelStage) => void;
  onBranch: (stage: RoadmapLevelStage) => void;
}) {
  const mapWidth = Math.max(300, width - 48);
  const points = overviewPoints(graph.length, mapWidth);
  const path = overviewPath(points, mapWidth);
  const height = (points[points.length - 1]?.y ?? 0) + 110;
  return (
    <div className="rm-overview" data-testid="curriculum-overview" aria-label="Curriculum overview">
      <div className="rm-overview-intro">
        <Compass size={20} />
        <div>
          <h2>The music ahead of you</h2>
          <p>Follow the lessons. Explore the repertoire along the way.</p>
        </div>
      </div>
      <div className="rm-overview-map" style={{ height }}>
        <svg width="100%" height={height} aria-hidden="true">
          <path d={path} fill="none" stroke="#070f1d" strokeWidth="20" transform="translate(0 6)" />
          <path d={path} fill="none" stroke="#3d5267" strokeWidth="13" />
          <path d={path} fill="none" stroke="#26394c" strokeWidth="10" />
        </svg>
        {graph.map((stage, i) => (
          <div
            key={stage.levelNumber}
            className={`rm-overview-node is-${stage.lessonState}`}
            style={{ left: points[i].x, top: points[i].y }}
          >
            <button
              className={`rm-overview-level ${selected === stage.levelNumber ? 'is-selected' : ''}`}
              onClick={() => onLesson(stage)}
              aria-label={`Go to Level ${stage.levelNumber}: ${stage.level.name}`}
            >
              {stage.levelNumber}
            </button>
            <span className="rm-overview-name">{stage.level.name}</span>
            {stage.repertoire && (
              <button
                className={`rm-overview-branch ${stage.repertoire.availability === 'future' ? 'is-future' : ''}`}
                onClick={() => onBranch(stage)}
                aria-label={`Explore Level ${stage.levelNumber} repertoire, ${stage.repertoire.pieces.length} ${stage.repertoire.pieces.length === 1 ? 'piece' : 'pieces'}`}
              >
                <BookOpen size={14} />
                <span>
                  {stage.repertoire.pieces.length}{' '}
                  {stage.repertoire.pieces.length === 1 ? 'piece' : 'pieces'}
                </span>
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RoadmapStages() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const graph = useSelector(selectCurriculumGraph);
  const data = useSelector((state: CurriculumState) => state.mainAppReducer);
  // Completed tutorial chapters per level (hydrated at startup), for the shared
  // tutorial-or-gameplay Play decision - see resolvePlayRoute / MUS-393.
  const completedTutorials = useSelector((state: RootState) => state.tutorialReducer?.completed);
  const isMobile = useMediaQuery('(max-width:600px), (max-height:480px)');
  const isMenuMobile = useMediaQuery('(max-width:480px), (max-height:480px)');
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const debugMidi = useFeatureFlag(FeatureFlag.DEBUG_DUMMY_MIDI);
  const [menuOpen, setMenuOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(
    new URLSearchParams(location.search).has('show-thank-you'),
  );
  const selectedLesson = graph.find((s) => s.levelIndex === data.levelSelect) ?? graph[0];
  const visitKey = `${data.userData?.user_id ?? 'session'}:${data.unitSelect}`;
  const [selection, setSelection] = useState<RoadmapSelection>(
    () =>
      visits.get(visitKey)?.selection ?? {
        kind: 'lesson',
        levelNumber: selectedLesson?.levelNumber ?? 0,
      },
  );
  const [overview, setOverview] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [scroll, setScroll] = useState(0);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [promoted, setPromoted] = useState<number[]>([]);
  const [showStages, setShowStages] = useState(() => visits.get(visitKey)?.showStages ?? true);
  const [explored, setExplored] = useState<number[]>(() => visits.get(visitKey)?.explored ?? []);
  const viewportRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; scroll: number } | null>(null);
  const pathScroll = useRef<number | null>(visits.get(visitKey)?.scroll ?? null);
  const latestVisit = useRef<Visit>({ selection, scroll: 0, showStages, explored });
  const previousComplete = useRef<Set<number> | null>(null);
  const previousBranchFocus = useRef<number | null>(null);
  const previousGraph = useRef('');
  const graphKey = graph.map((s) => s.levelNumber).join(',');
  const stage = graph.find((s) => s.levelNumber === selection.levelNumber) ?? selectedLesson;
  const branchFocused = !overview && selection.kind !== 'lesson' && Boolean(stage?.repertoire);
  const openStages = useMemo(
    () =>
      new Set(
        graph
          .filter(
            (node) =>
              node.repertoire &&
              ((showStages &&
                (node.repertoire.availability !== 'future' ||
                  explored.includes(node.levelNumber))) ||
                (selection.kind !== 'lesson' && selection.levelNumber === node.levelNumber)),
          )
          .map((node) => node.levelNumber),
      ),
    [graph, showStages, explored, selection.kind, selection.levelNumber],
  );
  const layout = useMemo(() => journeyLayout(graph, size, openStages), [graph, size, openStages]);
  const { points, width } = layout;
  const layoutKey = points.map((point) => point.x).join(',');
  const selectedPiece =
    selection.kind === 'piece'
      ? stage?.repertoire?.pieces.find((p) => p.piece.id === selection.pieceId)
      : undefined;
  // The unit switcher is a development tool, matching the legacy roadmap.
  const showUnitPicker =
    !isCapacitorNative() &&
    (process.env.REACT_APP_NODE_ENV === 'local' || process.env.REACT_APP_NODE_ENV === 'dev');

  useEffect(() => {
    if (data.userData?.subscription_status === '') navigate('/welcome');
  }, [data.userData?.subscription_status, navigate]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const measure = () => setSize({ width: viewport.clientWidth, height: viewport.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [graph.length > 0]);

  useEffect(() => {
    if (
      !stage ||
      (selection.kind !== 'lesson' && !stage.repertoire) ||
      (selection.kind === 'piece' && !selectedPiece)
    ) {
      if (selectedLesson) setSelection({ kind: 'lesson', levelNumber: selectedLesson.levelNumber });
    }
  }, [stage, selection, selectedPiece, selectedLesson]);

  useEffect(() => {
    if (
      selection.kind === 'lesson' &&
      selectedLesson &&
      selection.levelNumber !== selectedLesson.levelNumber
    ) {
      setSelection({ kind: 'lesson', levelNumber: selectedLesson.levelNumber });
    }
  }, [selectedLesson, selection]);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element || !points.length || !size.width) return;
    const changed = previousGraph.current && previousGraph.current !== `${graphKey}:${layoutKey}`;
    previousGraph.current = `${graphKey}:${layoutKey}`;
    const point =
      points[graph.findIndex((node) => node.levelNumber === stage?.levelNumber)] ?? points[0];
    element.scrollLeft =
      changed || branchFocused
        ? point.x - size.width / 2
        : (pathScroll.current ?? point.x - size.width / 2);
    const platform = stage && layout.platforms.get(stage.levelNumber);
    element.scrollTop =
      branchFocused && platform && platform.y + platform.height > size.height
        ? platform.y + platform.height - size.height + 16
        : 0;
    setScroll(element.scrollLeft);
    if (!branchFocused) pathScroll.current = element.scrollLeft;
    // Geometry changes keep the selection centered; ordinary manual panning stays untouched.
  }, [size.width, size.height, graphKey, layoutKey, branchFocused, overview]);

  useEffect(() => {
    const focusLevel = branchFocused ? stage.levelNumber : null;
    const changed = previousBranchFocus.current !== focusLevel;
    previousBranchFocus.current = focusLevel;
    if (!changed || overview) return;
    const frame = requestAnimationFrame(() => {
      const root = viewportRef.current;
      const target = branchFocused
        ? (root?.querySelector<HTMLElement>(
            '.rm-stage.is-selected .rm-piece[aria-pressed="true"]',
          ) ?? root?.querySelector<HTMLElement>('.rm-stage.is-selected .rm-piece'))
        : root?.querySelector<HTMLElement>('.rm-level[aria-pressed="true"]');
      target?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [branchFocused, stage?.levelNumber, overview]);

  latestVisit.current = { selection, scroll: pathScroll.current ?? scroll, showStages, explored };
  useEffect(
    () => () => {
      visits.set(visitKey, latestVisit.current);
    },
    [visitKey],
  );

  useEffect(() => {
    const complete = new Set(
      graph.filter((s) => s.lessonState === 'complete').map((s) => s.levelNumber),
    );
    const newlyComplete = graph.filter(
      (s) =>
        s.repertoire &&
        complete.has(s.levelNumber) &&
        previousComplete.current &&
        !previousComplete.current.has(s.levelNumber),
    );
    previousComplete.current = complete;
    if (!newlyComplete.length) return;
    setPromoted(newlyComplete.map((s) => s.levelNumber));
  }, [graph]);

  // A catalog/progress refresh must not cancel dismissal of a pulse already showing.
  useEffect(() => {
    if (!promoted.length) return;
    const timer = setTimeout(() => setPromoted([]), 1800);
    return () => clearTimeout(timer);
  }, [promoted]);

  const center = useCallback(
    (target: RoadmapLevelStage, smooth = true, focusStage = false) => {
      const point = points[graph.findIndex((s) => s.levelNumber === target.levelNumber)];
      const platform = layout.platforms.get(target.levelNumber);
      if (point)
        scrollRef.current?.scrollTo({
          left: point.x - size.width / 2,
          top:
            focusStage && platform
              ? Math.max(0, platform.y + platform.height - size.height + 16)
              : 0,
          behavior: smooth && !reducedMotion ? 'smooth' : 'auto',
        });
    },
    [graph, points, size, reducedMotion, layout.platforms],
  );

  const chooseLesson = useCallback(
    (target: RoadmapLevelStage) => {
      setOverview(false);
      setSelection({ kind: 'lesson', levelNumber: target.levelNumber });
      setSheetOpen(true);
      dispatch(appActions.setLevelSelect({ levelSelect: target.levelIndex }));
      pathScroll.current = Math.max(0, (points[graph.indexOf(target)]?.x ?? 0) - size.width / 2);
      center(target);
    },
    [dispatch, points, graph, size.width, center],
  );

  const chooseBranch = useCallback(
    (target: RoadmapLevelStage) => {
      if (!target.repertoire) return;
      if (!branchFocused) pathScroll.current = scrollRef.current?.scrollLeft ?? pathScroll.current;
      setOverview(false);
      setExplored((current) =>
        current.includes(target.levelNumber) ? current : [...current, target.levelNumber],
      );
      setSelection({ kind: 'stage', levelNumber: target.levelNumber });
      setSheetOpen(true);
      setExpanded(false);
      center(target, false, true);
    },
    [branchFocused, center],
  );

  const backToPath = useCallback(() => {
    setOverview(false);
    if (selectedLesson) setSelection({ kind: 'lesson', levelNumber: selectedLesson.levelNumber });
    setExpanded(false);
    setSheetOpen(true);
  }, [selectedLesson]);

  const playLesson = useCallback(
    (target: RoadmapLevelStage, replay = false) => {
      dispatch(appActions.setLevelSelect({ levelSelect: target.levelIndex }));
      dispatch(appActions.fetchTutorialData());
      const completedChapters = Immutable.get(
        completedTutorials,
        target.levelNumber,
      ) as CompletedChapters;
      navigate(
        replay
          ? '/tutorial'
          : resolvePlayRoute(target.levelNumber, target.highestTierComplete, completedChapters),
      );
    },
    [dispatch, navigate, completedTutorials],
  );
  const playPiece = useCallback(
    (piece: RoadmapPiece) => {
      dispatch(repertoireActions.setRepertoireSelect({ repertoireIdSelect: piece.piece.id }));
      navigate('/repertoire-play');
    },
    [dispatch, navigate],
  );
  const choosePiece = useCallback(
    (piece: RoadmapPiece, target = stage) => {
      if (!target) return;
      if (!branchFocused) pathScroll.current = scrollRef.current?.scrollLeft ?? pathScroll.current;
      setExplored((current) =>
        current.includes(target.levelNumber) ? current : [...current, target.levelNumber],
      );
      setSelection({ kind: 'piece', levelNumber: target.levelNumber, pieceId: piece.piece.id });
      center(target, true, true);
      setSheetOpen(true);
      dispatch(repertoireActions.setRepertoireSelect({ repertoireIdSelect: piece.piece.id }));
    },
    [dispatch, stage, branchFocused, center],
  );
  const browse = useCallback(
    (target = stage) => {
      if (!target) return;
      dispatch(appActions.setRepPageSelected('Browse'));
      dispatch(repertoireActions.setVerticalScroll({ page: 'browse', position: 0 }));
      navigate(`/repertoire?level=${target.levelNumber}`, { state: { from: 'roadmap' } });
    },
    [dispatch, navigate, stage],
  );
  const play = useCallback(() => {
    if (selectedPiece) playPiece(selectedPiece);
    else if (selection.kind === 'lesson' && stage) playLesson(stage);
    else if (stage) browse();
  }, [selectedPiece, playPiece, selection.kind, stage, playLesson, browse]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // A focused control that already acted on the key (Full Screen, a level) keeps it.
      if (event.defaultPrevented) return;
      const target = event.target as HTMLElement;
      if (
        target.closest(
          'input, textarea, select, [contenteditable="true"], [role="dialog"], [role="listbox"], [role="slider"]',
        )
      )
        return;
      if (event.key === 'Escape') {
        if (expanded) setExpanded(false);
        else backToPath();
        return;
      }
      if (target.closest('button') && !target.closest('.rm-level, .rm-piece')) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const focusedStage = target.closest<HTMLElement>('[data-stage-level]');
        const focusedLevel = target.closest<HTMLElement>('[data-roadmap-level-button]');
        const owner = focusedStage
          ? graph.find((node) => node.levelNumber === Number(focusedStage.dataset.stageLevel))
          : branchFocused && !focusedLevel
            ? stage
            : undefined;
        if (owner?.repertoire) {
          const pieces = owner.repertoire.pieces;
          const focusedId =
            target.closest<HTMLElement>('[data-roadmap-piece]')?.dataset.roadmapPiece;
          const index = pieces.findIndex(
            (piece) => piece.piece.id === (focusedId ? Number(focusedId) : selectedPiece?.piece.id),
          );
          const next = pieces[Math.max(0, Math.min(pieces.length - 1, index + direction))];
          choosePiece(next, owner);
          requestAnimationFrame(() =>
            viewportRef.current
              ?.querySelector<HTMLElement>(`[data-roadmap-piece="${next.piece.id}"]`)
              ?.focus({ preventScroll: true }),
          );
        } else if (stage) {
          const index = focusedLevel
            ? graph.findIndex(
                (node) => node.levelNumber === Number(focusedLevel.dataset.roadmapLevelButton),
              )
            : graph.indexOf(stage);
          const next = graph[Math.max(0, Math.min(graph.length - 1, index + direction))];
          chooseLesson(next);
          requestAnimationFrame(() =>
            viewportRef.current
              ?.querySelector<HTMLElement>(`[data-roadmap-level-button="${next.levelNumber}"]`)
              ?.focus({ preventScroll: true }),
          );
        }
      } else if (isSpaceForThePage(event) && !event.repeat) {
        event.preventDefault();
        play();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    backToPath,
    branchFocused,
    chooseLesson,
    choosePiece,
    expanded,
    graph,
    play,
    selectedPiece,
    stage,
  ]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element || overview) return;
    const wheel = (event: WheelEvent) => {
      if (
        !event.ctrlKey &&
        element.scrollHeight <= element.clientHeight + 1 &&
        Math.abs(event.deltaY) > Math.abs(event.deltaX)
      ) {
        event.preventDefault();
        element.scrollLeft += event.deltaY;
      }
    };
    element.addEventListener('wheel', wheel, { passive: false });
    return () => element.removeEventListener('wheel', wheel);
  }, [overview, graph.length > 0]);

  if (!graph.length || !stage)
    return (
      <div className="rm-loading" role="status">
        Loading your musical journey…
      </div>
    );

  return (
    <div className="rm-app" data-testid="roadmap-stages">
      <Menu
        selected="Roadmap"
        mobileOpen={menuOpen}
        setMobileOpen={setMenuOpen}
        hideMobileTrigger
      />
      <Survey />
      {paymentOpen && <Confirmation setShowConfirmation={setPaymentOpen} />}
      <main className="rm-main">
        <header className="rm-header">
          <div className="rm-header-title">
            {isMenuMobile && <MobileMenuButton inline onClick={() => setMenuOpen(true)} />}
            <h1>Roadmap</h1>
          </div>
          <nav className="rm-toolbar" aria-label="Roadmap view">
            {branchFocused ? (
              <button className="rm-back" onClick={backToPath}>
                <ArrowLeft size={17} />
                Back to main path
              </button>
            ) : (
              <div className="rm-view-toggle">
                <button aria-pressed={!overview} onClick={backToPath}>
                  <Route size={16} />
                  Journey
                </button>
                <button
                  aria-pressed={overview}
                  onClick={() => {
                    pathScroll.current = scrollRef.current?.scrollLeft ?? pathScroll.current;
                    setOverview(true);
                  }}
                >
                  <Layers size={16} />
                  Overview
                </button>
              </div>
            )}
            <div className="rm-toolbar-right">
              {!overview && graph.some((node) => node.repertoire) && (
                <button
                  className="rm-stage-toggle"
                  aria-pressed={showStages}
                  aria-label="Keep repertoire stages open"
                  onClick={() => {
                    setShowStages(!showStages);
                    backToPath();
                  }}
                >
                  <Layers size={15} />
                  <span>Stages</span>
                  <span className="rm-toggle-track" aria-hidden="true" />
                </button>
              )}
              {showUnitPicker && (
                <UnitPicker
                  unit={data.unitSelect}
                  units={[1, 2]}
                  onChange={(unitSelect) => dispatch(appActions.setUnitSelect({ unitSelect }))}
                />
              )}
              {debugMidi && <button onClick={() => navigate('/piano-debug')}>MIDI debug</button>}
            </div>
          </nav>
          <RoadmapMinimap
            graph={graph}
            points={points}
            worldWidth={width}
            viewportWidth={size.width}
            scroll={scroll}
            selectedLevel={selectedLesson.levelNumber}
            disabled={overview}
            onScroll={(left) => {
              if (scrollRef.current) scrollRef.current.scrollLeft = left;
            }}
          />
        </header>
        <div
          className={`rm-viewport ${overview ? 'is-overview' : ''}`}
          ref={viewportRef}
          data-testid="roadmap-viewport"
        >
          <div className="rm-contours" aria-hidden="true" />
          {overview ? (
            <Overview
              graph={graph}
              width={size.width}
              selected={selectedLesson.levelNumber}
              onLesson={chooseLesson}
              onBranch={chooseBranch}
            />
          ) : (
            <>
              <div
                className="rm-scroll"
                ref={scrollRef}
                onScroll={(event) => {
                  const x = event.currentTarget.scrollLeft;
                  setScroll(x);
                  if (!branchFocused) pathScroll.current = x;
                }}
                onPointerDown={(event) => {
                  if (
                    event.pointerType === 'mouse' &&
                    !(event.target as HTMLElement).closest('button')
                  ) {
                    drag.current = { x: event.clientX, scroll: event.currentTarget.scrollLeft };
                    event.currentTarget.setPointerCapture(event.pointerId);
                  }
                }}
                onPointerMove={(event) => {
                  if (drag.current)
                    event.currentTarget.scrollLeft =
                      drag.current.scroll + drag.current.x - event.clientX;
                }}
                onPointerUp={() => {
                  drag.current = null;
                }}
                onPointerCancel={() => {
                  drag.current = null;
                }}
              >
                <div
                  className={`rm-world ${layout.short ? 'is-short' : ''} ${layout.compact ? 'is-compact' : ''}`}
                  style={{ width, height: layout.height }}
                  data-testid="roadmap-world"
                >
                  <RoadmapScene viewportRef={viewportRef} />
                  <Ribbon
                    graph={graph}
                    points={points}
                    width={width}
                    height={layout.height}
                    platforms={layout.platforms}
                  />
                  {graph.map((node, i) => (
                    <div
                      key={node.levelNumber}
                      className={`rm-level-group is-${node.lessonState} ${promoted.includes(node.levelNumber) ? 'is-promoted' : ''}`}
                      style={{ left: points[i].x, top: points[i].y }}
                    >
                      <button
                        type="button"
                        className={`rm-level ${selectedLesson.levelNumber === node.levelNumber ? 'is-selected' : ''}`}
                        data-roadmap-level-button={node.levelNumber}
                        data-level-index={node.levelIndex}
                        aria-label={`Select level ${node.levelNumber}: ${node.level.name}`}
                        aria-describedby={`roadmap-level-${node.levelNumber}-status`}
                        aria-pressed={selectedLesson.levelNumber === node.levelNumber}
                        onClick={() => chooseLesson(node)}
                        onDoubleClick={() => playLesson(node)}
                      >
                        <span className="rm-level-name" title={node.level.name}>
                          {node.level.name}
                        </span>
                        <span className="rm-level-disc">{node.levelNumber}</span>
                        {/* Sits between the name and the button, inside the label clearance. */}
                        <span
                          id={`roadmap-level-${node.levelNumber}-status`}
                          className="rm-level-state rm-hover-progress"
                        >
                          {node.lessonState === 'complete' ? (
                            <>
                              <Check size={12} />
                              COMPLETE
                            </>
                          ) : node.levelNumber === 0 ? (
                            'TUTORIAL'
                          ) : node.lessonState === 'active' ? (
                            `${Math.round(Math.min(100, (node.highestTierComplete / Math.max(1, node.tierCount)) * 100))}% COMPLETE`
                          ) : (
                            'NOT STARTED'
                          )}
                        </span>
                      </button>
                      {node.repertoire && !openStages.has(node.levelNumber) && (
                        <button
                          type="button"
                          className={`rm-pod is-${node.repertoire.state}`}
                          data-roadmap-branch={node.levelNumber}
                          aria-label={`Explore Level ${node.levelNumber} repertoire, ${node.repertoire.pieces.length} ${node.repertoire.pieces.length === 1 ? 'piece' : 'pieces'}, ${node.repertoire.availability === 'future' ? 'ahead on your path' : `${node.repertoire.completedCount} complete`}`}
                          onClick={() => chooseBranch(node)}
                        >
                          <span className="rm-pod-title">
                            <BookOpen size={19} />
                            Repertoire <ChevronRight size={13} />
                          </span>
                          <span className="rm-pod-count">
                            {node.repertoire.pieces.length}{' '}
                            {node.repertoire.pieces.length === 1 ? 'piece' : 'pieces'}
                            <span>·</span>
                            {node.repertoire.availability === 'future'
                              ? 'Up ahead'
                              : `${node.repertoire.completedCount} complete`}
                          </span>
                        </button>
                      )}
                    </div>
                  ))}
                  {graph.map((node) => {
                    const position = layout.platforms.get(node.levelNumber);
                    return (
                      position && (
                        <RepertoireStage
                          key={node.levelNumber}
                          stage={node}
                          position={position}
                          compact={layout.compact}
                          active={branchFocused && stage.levelNumber === node.levelNumber}
                          promoted={promoted.includes(node.levelNumber)}
                          selectedId={
                            stage.levelNumber === node.levelNumber
                              ? selectedPiece?.piece.id
                              : undefined
                          }
                          onOpen={() => chooseBranch(node)}
                          onSelect={(piece) => choosePiece(piece, node)}
                          onPlay={playPiece}
                          onBrowse={() => browse(node)}
                        />
                      )
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
        {isMobile && expanded && (
          <button
            className="rm-sheet-backdrop"
            aria-label="Collapse roadmap details"
            onClick={() => setExpanded(false)}
          />
        )}
        {(!isMobile || sheetOpen) && (
          <RoadmapDetails
            stage={stage}
            selection={selection}
            piece={selectedPiece}
            onPlay={play}
            onReplay={() => playLesson(stage, true)}
            onBrowse={() => browse()}
            onClose={() => {
              setSheetOpen(false);
              setExpanded(false);
            }}
            mobile={isMobile}
            expanded={expanded}
            onExpand={setExpanded}
          />
        )}
        {isMobile && !sheetOpen && (
          <button className="rm-reopen-details" onClick={() => setSheetOpen(true)}>
            Show selected details
          </button>
        )}
        <span className="rm-sr-only" role="status">
          {promoted.length
            ? `Level ${promoted.join(', ')} complete. Repertoire is ready to explore.`
            : ''}
        </span>
      </main>
    </div>
  );
}
